/**
 * Extended Dashboard Expression Evaluator
 * Adds support for evaluating GroupExpressionStatus
 */

const isSystemOnline = (data, thresholdMinutes = 1) => {
  // Check if there's valid telemetry data
  if (!data || !data.Telemetry || !Array.isArray(data.Telemetry) || data.Telemetry.length === 0) {
    return false;
  }

  // Get the current timestamp in milliseconds
  const currentTimestamp = Date.now();

  // Calculate the threshold in milliseconds (default 1 minute)
  const thresholdMillis = thresholdMinutes * 60 * 1000;

  // Find the most recent telemetry timestamp
  let mostRecentTimestamp = 0;

  for (const item of data.Telemetry) {
    if (item.timestamp && item.timestamp > mostRecentTimestamp) {
      mostRecentTimestamp = item.timestamp;
    }
  }

  // If no valid timestamps were found
  if (mostRecentTimestamp === 0) {
    return false;
  }

  // Check if the most recent telemetry is within the threshold
  return (currentTimestamp - mostRecentTimestamp) <= thresholdMillis;
};

// Function to get system status with additional info
const getSystemStatus = (data, thresholdMinutes = 1) => {
  // Check if there's valid telemetry data
  if (!data || !data.LastTelemetryTimestamp || data.LastTelemetryTimestamp === 0) {
    return {
      isOnline: false,
      status: "Offline",
      lastSeenTimestamp: null,
      timeSinceLastSeen: null
    };
  }

  // Get the current timestamp in milliseconds
  const currentTimestamp = Date.now();

  // Find the most recent telemetry timestamp
  let mostRecentTimestamp = data.LastTelemetryTimestamp || 0;
  // If no valid timestamps were found
  if (mostRecentTimestamp === 0) {
    return {
      isOnline: false,
      status: "Offline",
      lastSeenTimestamp: null,
      timeSinceLastSeen: null
    };
  }

  // Calculate time difference in milliseconds
  const timeDiff = currentTimestamp - mostRecentTimestamp;

  // Calculate the threshold in milliseconds
  const thresholdMillis = thresholdMinutes * 60 * 1000;

  // Determine if the system is online
  const isOnline = timeDiff <= thresholdMillis;

  return {
    isOnline,
    status: isOnline ? "Online" : "Offline",
    lastSeenTimestamp: mostRecentTimestamp,
    timeSinceLastSeen: timeDiff
  };
};


// Function to evaluate group expressions based on the evaluated details
const evaluateGroupExpressions = (dashboardGroups) => {
  if (!dashboardGroups || !Array.isArray(dashboardGroups)) {
    return dashboardGroups;
  }

  // Process each group
  dashboardGroups.forEach(group => {
    // Process subgroups if they exist
    if (group.SubGroups && Array.isArray(group.SubGroups)) {
      group.SubGroups.forEach(subGroup => {
        // Only process if there's a GroupExpressionStatus and evaluated details
        if (subGroup.GroupExpressionStatus && subGroup.Details &&
          Array.isArray(subGroup.Details) && subGroup.Details.length > 0) {

          // Evaluate the group status expression
          try {
            const groupStatusExpression = processGroupStatusExpression(
              subGroup.GroupExpressionStatus,
              subGroup.Details
            );

            subGroup.EvaluatedGroupStatus = evaluateStatusExpression(groupStatusExpression);
          } catch (error) {
            console.error(`Error evaluating group status for ${subGroup.Name}:`, error);
            subGroup.EvaluatedGroupStatus = 'Unknown';
          }
        }

        // Recursively process nested subgroups if needed
        if (subGroup.SubGroups && Array.isArray(subGroup.SubGroups)) {
          evaluateGroupExpressions([subGroup]);
        }
      });
    }

    // in the case of a detail section
    if (group.Details && Array.isArray(group.Details)) {
      try {
        const groupStatusExpression = processGroupStatusExpression(
          group.GroupExpressionStatus,
          group.Details
        );

        group.EvaluatedGroupStatus = evaluateStatusExpression(groupStatusExpression);
      } catch (error) {
        group.EvaluatedGroupStatus = 'Unknown';
      }
    }
  });

  return dashboardGroups;
};

// Process the group status expression with access to detail values
const processGroupStatusExpression = (expression, details) => {
  // Replace group functions with their values
  let processedExpression = expression;

  // Process Min, Max, Average, etc. functions
  const functionRegex = /{([^{}]+)}/g;
  let match;

  while ((match = functionRegex.exec(expression)) !== null) {
    const functionCall = match[1];



    const functionResult = processGroupFunction(functionCall, details);
    processedExpression = processedExpression.replace(match[0], functionResult);
  }

  return processedExpression;
};

// Process functions that operate on the group's details
const processGroupFunction = (functionCall, details) => {
  // Extract function name and parameter
  const functionMatch = functionCall.match(/([A-Za-z]+)\('([^']+)'\)/);

  if (!functionMatch) {
    throw new Error(`Invalid group function format: ${functionCall}`);
  }

  const [, functionName, paramName] = functionMatch;

  // We're only extracting values from the details' EvaluatedValue field
  if (paramName !== 'ExpressionValue') {
    throw new Error(`Group functions can only operate on 'ExpressionValue'`);
  }

  // Extract the evaluated values from the details
  const values = details
    .filter(detail => detail.EvaluatedValue !== undefined && detail.EvaluatedValue !== null && detail.EvaluatedValue !== '-')
    .map(detail => {
      // Convert string values to numbers if possible
      const value = detail.EvaluatedValue;
      return typeof value === 'string' ? parseFloat(value) || "-" : value;
    });
  if (values.length === 0) {
    return "-"; // Default value if no valid detail values
  }

  // Apply the appropriate function to the values
  switch (functionName) {
    case 'Min':
      return Math.min(...values);
    case 'Max':
      return Math.max(...values);
    case 'Average':
      return values.reduce((sum, val) => sum + val, 0) / values.length;
    case 'Sum':
      return values.reduce((sum, val) => sum + val, 0);
    case 'Count':
      return values.length;
    default:
      throw new Error(`Unknown group function: ${functionName}`);
  }
};

// Enhanced evaluatedOverviewData function with group status evaluation
export const evaluatedOverviewData = (overviewData, mainConfigData = null) => {
  let dashboardConfig = mainConfigData || (overviewData && overviewData.Dashboard);

  // Add system online status to the dashboard config
  if (dashboardConfig && overviewData && overviewData.Data) {
    dashboardConfig.SystemStatus = getSystemStatus(overviewData.Data);
  }

  // First evaluate all detail expressions
  if (dashboardConfig && dashboardConfig.DashboardGroups) {
    dashboardConfig.DashboardGroups.forEach(mg => {
      if (mg.SubGroups && mg.SubGroups.length > 0) {
        mg.SubGroups.forEach(sg => {
          if (sg.Details && sg.Details.length > 0) {
            sg.Details = processDashboard(sg.Details, overviewData.Data);
          }
        });
      }

      if (mg.Details && mg.Details.length > 0) {
        mg.Details = processDashboard(mg.Details, overviewData.Data);
      }
    });
  }

  if (dashboardConfig && dashboardConfig.DashboardDetails && dashboardConfig.DashboardDetails.length > 0) {
    dashboardConfig.DashboardDetails = processDashboard(dashboardConfig.DashboardDetails, overviewData.Data);
  }

  // After all details have been evaluated, evaluate the group status expressions
  if (dashboardConfig && dashboardConfig.DashboardGroups) {
    dashboardConfig.DashboardGroups = evaluateGroupExpressions(dashboardConfig.DashboardGroups);
  }

  return dashboardConfig;
};

// Example usage - Evaluate all expressions in the dashboard
const processDashboard = (dashboardDetails, data) => {
  try {
    // Parse input if strings are provided
    const parsedDashboardDetails = typeof dashboardDetails === 'string'
      ? JSON.parse(dashboardDetails)
      : dashboardDetails;

    const parsedData = typeof data === 'string'
      ? JSON.parse(data)
      : data;

    // Evaluate all dashboard expressions
    return evaluateDashboardExpressions(parsedDashboardDetails, parsedData);
  } catch (error) {
    return null;
  }
};

// Main function to evaluate dashboard expressions
const evaluateDashboardExpressions = (dashboardDetails, data) => {
  const evaluatedDashboard = dashboardDetails.map(detail => {
    // Create a copy of the dashboard detail
    const evaluatedDetail = { ...detail };

    // Evaluate the expression
    try {
      if (data && !hasData(data)) {
        evaluatedDetail.EvaluatedValue = "-";
        if (detail.ExpressionValueStatus) {
          evaluatedDetail.EvaluatedStatus = '-';
        }
        return evaluatedDetail;
      }
      const expressionValue = evaluateExpression(detail.ExpressionValue, data);
      evaluatedDetail.EvaluatedValue = expressionValue;


      // Evaluate status if present
      if (detail.ExpressionValueStatus && expressionValue !== null && expressionValue !== undefined) {
        let statusExpression = detail.ExpressionValueStatus.replace(/{([^{}]+)}/g, (fullMatch, functionCall) => {
          if (functionCall.startsWith('Result(')) return fullMatch;
          try { return processFunctionCall(functionCall, data); }
          catch { return fullMatch; }
        });
        statusExpression = statusExpression.replace(/{Result\('ExpressionValue'\)}/g, expressionValue);
        evaluatedDetail.EvaluatedStatus = evaluateStatusExpression(statusExpression);
      }
      else if (expressionValue === null || expressionValue === undefined) {
        {
          evaluatedDetail.EvaluatedStatus = "-";
        }
      }
    } catch (error) {
      evaluatedDetail.EvaluatedValue = null;
      evaluatedDetail.Error = error.message;
    }

    return evaluatedDetail;
  });

  return evaluatedDashboard;
};

// Function to evaluate the status expression
const evaluateStatusExpression = statusExpression => {
  // Replace ternary operators with JavaScript equivalent
  const jsExpression = statusExpression
    .replace(/\{/g, '')
    .replace(/\}/g, '');

    console.log('EXACT:', JSON.stringify(jsExpression));

  try {
    // Use Function constructor to evaluate the expression safely
    return Function(`"use strict"; return (${jsExpression})`)();
  } catch (error) {
    console.log('error', statusExpression, error);
    return '-';
  }
};

// Main function to parse and evaluate an expression
const evaluateExpression = (expression, data) => {
  let processedExpression = expression;

  // Iteratively resolve innermost {} until none remain
  const innermostRegex = /{([^{}]+)}/g;
  let prev;
  do {
    prev = processedExpression;
    processedExpression = processedExpression.replace(/{([^{}]+)}/g, (fullMatch, functionCall) => {
      try {
        return processFunctionCall(functionCall, data);
      } catch {
        return fullMatch; // leave unchanged if it fails
      }
    });
  } while (processedExpression !== prev && processedExpression.includes('{'));

  try {
    return Function(`"use strict"; return (${processedExpression})`)();
  } catch (error) {
    return null;
  }
};

// Process a single function call within an expression
const processFunctionCall = (functionCall, data) => {
  if (functionCall.startsWith('Result(')) {
    const paramMatch = functionCall.match(/Result\('([^']+)'\)/);
    if (paramMatch && paramMatch[1] === 'ExpressionValue') {
      return "{Result('ExpressionValue')}";
    }
    return 0;
  }

  // Standard format: FunctionName('param', 'section')
  const functionMatch = functionCall.match(/([A-Za-z]+)\('([^']+)',\s*'([^']+)'\)/);
  if (functionMatch) {
    const [, functionName, paramName, section] = functionMatch;
    switch (functionName) {
      case 'Average': return calculateAverage(paramName, section, data);
      case 'DifferentialValue': return calculateDifferential(paramName, section, data);
      case 'Min': return calculateMin(paramName, section, data);
      case 'Max': return calculateMax(paramName, section, data);
      case 'Mean': return calculateMean(paramName, section, data);
      case 'Mode': return calculateMode(paramName, section, data);
      case 'Sum': return calculateSum(paramName, section, data);
      case 'LastValue': return getLastValue(paramName, section, data);
      case 'FirstValue': return getFirstValue(paramName, section, data);
      case 'IsEventActive': return getIsEventActive(paramName, section, data);
      default: throw new Error(`Unknown function: ${functionName}`);
    }
  }

  // ✅ NEW: Computed args format: FunctionName(num1, num2, ...)
  // Handles cases like Min(0.5, 1.2) after inner functions have been resolved
  const computedMatch = functionCall.match(/^([A-Za-z]+)\((.+)\)$/);
  if (computedMatch) {
    const [, functionName, argsStr] = computedMatch;
    const args = argsStr
      .split(',')
      .map(s => s.trim())
      .map(s => (s === '-' ? NaN : parseFloat(s)))
      .filter(v => !isNaN(v));

    if (args.length === 0) return '-';

    switch (functionName) {
      case 'Min': return Math.min(...args);
      case 'Max': return Math.max(...args);
      case 'Average': return args.reduce((a, b) => a + b, 0) / args.length;
      case 'Sum': return args.reduce((a, b) => a + b, 0);
      case 'Count': return args.length;
      default: throw new Error(`Unknown computed function: ${functionName}`);
    }
  }

  throw new Error(`Invalid function format: ${functionCall}`);
};

// Get the appropriate dataset based on section type
const getDataset = (paramName, section, data) => {
  // Check if the section indicates telemetry data
  if (['value', 'state', 'derived'].includes(section)) {
    return {
      dataset: data.Telemetry,
      type: 'telemetry',
      section
    };
  }

  // Check if it's a summary section
  const summarySections = ['min_value', 'max_value', 'volume_processed', 'cycle_count', 'hours_used', 'custom_summary'];
  if (summarySections.includes(section)) {
    return {
      dataset: data.Summary,
      type: 'summary',
      section
    };
  }

  // Check if it's a alamr section
  const alarmSections = ['alarm'];
  if (alarmSections.includes(section)) {
    return {
      dataset: data.Alarms && data.Alarms.AlarmData || [],
      type: 'alarm',
      section
    };
  }
 

  throw new Error(`Unknown section: ${section}`);
};

// Extract values from the dataset based on parameter and section
const extractValues = (paramName, section, data) => {
  const { dataset, type, section: sectionName } = getDataset(paramName, section, data);

  // If no dataset, return empty array
  if (!dataset || dataset.length === 0) {
    return [];
  }

  // Extract values based on the type
  if (type === 'telemetry') {
    // For telemetry, directly extract the parameter value
    return dataset
      .filter(item => item[paramName] !== undefined)
      .map(item => item[paramName]);
  } else if (type === 'summary') {
    // For summary, we need to extract from the correct subsection
    return dataset
      .filter(item => item[sectionName] && item[sectionName][paramName] !== undefined)
      .map(item => item[sectionName][paramName]);
  }

  return [];
};

// Function implementations
const calculateAverage = (paramName, section, data) => {
  const values = extractValues(paramName, section, data);
  if (values.length === 0) return '-';
  const sum = values.reduce((acc, val) => acc + val, 0);
  return sum / values.length;
};

const calculateDifferential = (paramName, section, data) => {
  const { dataset, type, section: sectionName } = getDataset(paramName, section, data);

  if (!dataset || dataset.length < 2) return 0;

  // Sort by timestamp
  const sortedData = [...dataset].sort((a, b) => a.timestamp - b.timestamp);

  let firstValue, lastValue;



  if (type === 'telemetry') {
    if (sortedData[0][paramName] === undefined || sortedData[sortedData.length - 1][paramName] === undefined) {
      return "-";
    }
    firstValue = sortedData[0][paramName] || 0;
    lastValue = sortedData[sortedData.length - 1][paramName] || 0;
  } else if (type === 'summary') {
    if (sortedData[0][sectionName]?.[paramName] === undefined || sortedData[sortedData.length - 1][sectionName]?.[paramName] === undefined) {
      return "-";
    }
    firstValue = sortedData[0][sectionName]?.[paramName] || 0;
    lastValue = sortedData[sortedData.length - 1][sectionName]?.[paramName] || 0;
  }

  return lastValue - firstValue;
};

const calculateMin = (paramName, section, data) => {
  const values = extractValues(paramName, section, data);
  if (values.length === 0) return '-';

  return Math.min(...values);
};

const calculateMax = (paramName, section, data) => {
  const values = extractValues(paramName, section, data);
  if (values.length === 0) return '-';

  return Math.max(...values);
};

const calculateMean = (paramName, section, data) => {
  // Mean is the same as average for this implementation
  return calculateAverage(paramName, section, data);
};

const calculateMode = (paramName, section, data) => {
  const values = extractValues(paramName, section, data);
  if (values.length === 0) return '-';

  // Count frequency of each value
  const frequency = {};
  values.forEach(value => {
    frequency[value] = (frequency[value] || 0) + 1;
  });

  // Find the value with the highest frequency
  let maxFreq = 0;
  let mode = null;

  for (const [value, freq] of Object.entries(frequency)) {
    if (freq > maxFreq) {
      maxFreq = freq;
      mode = parseFloat(value);
    }
  }

  return mode;
};

const calculateSum = (paramName, section, data) => {
  const values = extractValues(paramName, section, data);
  if (values.length === 0) return '-';

  return values.reduce((acc, val) => acc + val, 0);
};

const getLastValue = (paramName, section, data) => {
  const { dataset, type, section: sectionName } = getDataset(paramName, section, data);

  if (!dataset || dataset.length === 0) return 0;

  // Sort by timestamp
  const sortedData = [...dataset].sort((a, b) => a.timestamp - b.timestamp);
  const lastItem = sortedData[sortedData.length - 1];
  if (type === 'telemetry') {
    return lastItem[paramName] == undefined ? "-" : lastItem[paramName];
  } else if (type === 'summary') {
    return lastItem[sectionName]?.[paramName] == undefined ? "-" : lastItem[sectionName]?.[paramName];
  }

  return 0;
};

const getFirstValue = (paramName, section, data) => {
  const { dataset, type, section: sectionName } = getDataset(paramName, section, data);

  if (!dataset || dataset.length === 0) return 0;

  // Sort by timestamp
  const sortedData = [...dataset].sort((a, b) => a.timestamp - b.timestamp);
  const firstItem = sortedData[0];

  if (type === 'telemetry') {
    return firstItem[paramName] == undefined ? "-" : firstItem[paramName];
  } else if (type === 'summary') {
    return firstItem[sectionName]?.[paramName] == undefined ? "-" : firstItem[sectionName]?.[paramName];
  }

  return 0;
};

//By default its NO or YES

const getIsEventActive = (paramName, section, data) => {
  // paramName = '4024', section = 'alarmstatus'
  const alarmStatus = data?.Alarms?.AlarmStatus;
  if (!alarmStatus || !Array.isArray(alarmStatus) || alarmStatus.length === 0) return 0;

  const match = alarmStatus.find(
    e => String(e.code) === String(paramName) && e.type === section
    //                                           'alarmstatus' === 'alarmstatus' ✓
  );
  return match && match.isActive ? 1 : 0;
};


// Helper function to check if data exists
const hasData = (dataObj) => {
  const telemetry = dataObj?.Telemetry;
  const summary = dataObj?.Summary;

  return (Array.isArray(telemetry) && telemetry.length > 0) ||
    (Array.isArray(summary) && summary.length > 0);
};


// Merge data

/**
 * Utility function to remove duplicates based on timestamp
 * @param {Array} dataArray - Array of data objects with timestamp property
 * @returns {Array} - Array with duplicates removed
 */
const removeDuplicatesByTimestamp = (dataArray) => {
  const seen = new Set();
  return dataArray.filter(entry => {
    if (seen.has(entry.timestamp)) {
      return false;
    }
    seen.add(entry.timestamp);
    return true;
  });
};

/**
 * Enhanced version with more merge options
 * @param {Object} existingDataset - The existing dataset
 * @param {Object} newDataset - The new dataset to merge
 * @param {Object} options - Configuration options
 * @param {number} options.hoursBack - Hours to go back (default: 24)
 * @param {boolean} options.removeDuplicates - Remove duplicate timestamps (default: true)
 * @param {boolean} options.sortByTime - Sort results by timestamp (default: true)
 * @param {string} options.mergeStrategy - 'latest' or 'current' time reference (default: 'current')
 * @returns {Object} - Merged and filtered dataset
 */

export const advancedMergeAndFilter = (existingDataset, newDataset, options = {}) => {
  const {
    hoursBack = 24,
    removeDuplicates = true,
    sortByTime = true,
    mergeStrategy = 'current' // 'current' or 'latest'
  } = options;

  // Merge arrays
  let mergedTelemetry = [
    ...existingDataset.Data.Telemetry,
    ...newDataset.Data.Telemetry
  ];

  let mergedSummary = [
    ...existingDataset.Data.Summary,
    ...newDataset.Data.Summary
  ];

  // Remove duplicates if requested
  if (removeDuplicates) {
    mergedTelemetry = removeDuplicatesByTimestamp(mergedTelemetry);
    mergedSummary = removeDuplicatesByTimestamp(mergedSummary);
  }

  // Sort by timestamp if requested
  if (sortByTime) {
    mergedTelemetry.sort((a, b) => a.timestamp - b.timestamp);
    mergedSummary.sort((a, b) => a.timestamp - b.timestamp);
  }

  // Determine cutoff time based on strategy
  let cutoffTime;
  if (mergeStrategy === 'latest') {
    const allTimestamps = [
      ...mergedTelemetry.map(entry => entry.timestamp),
      ...mergedSummary.map(entry => entry.timestamp)
    ];
    const latestTimestamp = allTimestamps.length > 0 ? Math.max(...allTimestamps) : Date.now();
    cutoffTime = latestTimestamp - (hoursBack * 60 * 60 * 1000);
  } else {
    cutoffTime = Date.now() - (hoursBack * 60 * 60 * 1000);
  }

  // Filter by cutoff time
  const filteredTelemetry = mergedTelemetry.filter(entry =>
    entry.timestamp >= cutoffTime
  );

  const filteredSummary = mergedSummary.filter(entry =>
    entry.timestamp >= cutoffTime
  );


  return {
    ...existingDataset,
    Data: {
      ...existingDataset.Data,
      ...newDataset.Data,
      Telemetry: filteredTelemetry,
      Summary: filteredSummary,
      Alarms: newDataset.Data.Alarms,
      LastSummaryTimestamp: filteredSummary.length > 0
        ? Math.max(...filteredSummary.map(entry => entry.timestamp))
        : existingDataset.Data.LastSummaryTimestamp
    }
  };
};